<!-- eslint-disable no-undef -->
<script setup>
  // Imports
  import { ref, onMounted, watch, computed } from 'vue';
  import { useStore } from 'vuex';
  import LoadingAnimation3 from '../loadingIcons/LoadingAnimation3.vue';
  import VerifyPhoneNumber from '@/components/modals/VerifyPhoneNumber.vue';
  import Data from '@/api/data';
  import Device from '@/api/device';
  import Users from '@/api/users';
  import InfoIcon from '@/components/common/InfoIcon.vue';
  import { useModalBackdropClose } from '@/composables/useModalBackdropClose';

  // Definitions
  const props = defineProps({
    dataID: {
      type: String,
      default: null
    },
    alertDetails: {
      type: Object,
      default: null
    }
  });

  const store = useStore();
  const user = computed(() => store.state.user);

  const emit = defineEmits(['alert-details', 'close']);

  const loading = ref(false);
  const canProceed = ref(false);
  // const entities = computed(() => user.value.entityData);
  const selectedEntity = ref(user.value.entity);
  const grabbingDevices = ref(true);
  const devices = ref([]);
  const grabbingSubcollections = ref(false);
  const subcollections = ref({});
  const subcollectionOptions = ref([]);
  const subcollectionFields = ref([]);
  const error = ref('');
  const name = ref(props.alertDetails?.name || '');
  const type = ref(props.alertDetails?.type || 'value');
  const selectedDevices = ref(props.alertDetails?.devices || []);
  const source = ref(props.alertDetails?.source || '');
  const field = ref(props.alertDetails?.condition?.field || '');
  const operator = ref(props.alertDetails?.condition?.operator || '');
  const constant = ref(props.alertDetails?.condition?.constant || 0);
  const alertOnce = ref(props.alertDetails?.interval === 0 || false);
  const days = ref(Math.floor(props.alertDetails?.interval/86400) || 0);
  const hours = ref(Math.floor((props.alertDetails?.interval%86400)/3600) || 0);
  const minutes = ref(Math.floor((props.alertDetails?.interval%3600)/60) || 0);
  const description = ref(props.alertDetails?.description || '');
  const level = ref(props.alertDetails?.level || '');
  const validationCountActivate = ref(props.alertDetails?.validationCount?.activate || 1);
  const validationCountDeactivate = ref(props.alertDetails?.validationCount?.deactivate || 1);
  const users = ref([]); // All users in the entity and sub-entities
  const owners = ref(props.alertDetails?.owners || []);
  const viewers = ref(
    props.alertDetails?.users?.filter(userId => !owners.value.includes(userId)) || []
  );
  const invitedUsers = ref([]); // Users invited to the alert
  const addedUser = ref('');
  const emailToggle = ref(user.value.notifications.alerts.emailEnable);
  const smsToggle = ref(user.value.notifications.alerts.smsEnable);
  const modal = ref('');

  // Step 2 search + sort
  const searchQuery = ref('');
  const sortColumn = ref(null);
  const sortDirection = ref('asc');

  const filteredSortedDevices = computed(() => {
    let result = devices.value;

    if (searchQuery.value) {
      const q = searchQuery.value.toLowerCase();
      result = result.filter(d =>
        (d.name ?? '').toLowerCase().includes(q) || (d.id ?? '').toLowerCase().includes(q)
      );
    }

    if (sortColumn.value) {
      result = [...result].sort((a, b) => {
        const av = a[sortColumn.value];
        const bv = b[sortColumn.value];
        const an = parseFloat(av);
        const bn = parseFloat(bv);
        const cmp = !isNaN(an) && !isNaN(bn) ? an - bn : String(av ?? '').localeCompare(String(bv ?? ''));
        return sortDirection.value === 'asc' ? cmp : -cmp;
      });
    }

    return result;
  });

  const toggleSort = (column) => {
    if (sortColumn.value === column) {
      if (sortDirection.value === 'asc') {
        sortDirection.value = 'desc';
      } else {
        sortColumn.value = null;
        sortDirection.value = 'asc';
      }
    } else {
      sortColumn.value = column;
      sortDirection.value = 'asc';
    }
  };

  const toggleDevice = (deviceId) => {
    const idx = selectedDevices.value.indexOf(deviceId);
    if (idx === -1) selectedDevices.value.push(deviceId);
    else selectedDevices.value.splice(idx, 1);
  };

  // Step wizard state
  const currentStep = ref(1);
  const nameLength = computed(() => 50 - name.value.length);
  const descriptionLength = computed(() => 250 - description.value.length);
  const steps = ref({
    1: {
      title: "Basic Info",
      completed: props.alertDetails ? true : false
    },
    2: {
      title: "Select Devices",
      completed: props.alertDetails ? true : false
    },
    3: {
      title: "Set Conditions",
      completed: props.alertDetails ? true : false
    },
    4: {
      title: "Alert Frequency",
      completed: props.alertDetails ? true : false
    },
    5: {
      title: "Share & Review",
      completed: props.alertDetails ? true : false
    }
  });

  // Lifecycle hooks
  onMounted(async() => {
    if (props.dataID) {
      selectedDevices.value = [props.dataID];
      await getSubcollections(props.dataID);
    }
  });

  // Methods
  const getUsers = async() => {
    const response = await Users.get('/?entity=' + user.value.entity);
    if (response.status === 'success') {
      users.value = response.details.users;
    }

    // Get the users for the sub-entities
    if (selectedEntity.value !== user.value.entity) {
      const response = await Users.get('/?entity=' + selectedEntity.value);
      if (response.status === 'success') {
        users.value.push(...response.details.users);
      }
    }

    // Display the other users connected to the alert
    if (props.alertDetails) {
      for (const alertUser of props.alertDetails.users) {
        const foundUser = users.value.find(listedUser => listedUser.id === alertUser);
        if (foundUser && props.alertDetails.owners.includes(alertUser)) {
          invitedUsers.value.push({
            // email: foundUser.email,
            ...foundUser,
            permission: 'owner'
          });
        }
        else if (foundUser) {
          invitedUsers.value.push({
            // email: foundUser.email,
            ...foundUser,
            permission: 'viewer'
          });
        }
      }
      
      // Remove already invited users from the users array to prevent duplicate selection
      users.value = users.value.filter(user => 
        !invitedUsers.value.some(invitedUser => invitedUser.id === user.id)
      );
    } else {
      //Current user should be removed form the users array and added to the owners array
      const foundUser = users.value.find(listedUser => listedUser.id === user.value.uid);
      users.value = users.value.filter(listedUser => listedUser.id !== user.value.uid);
      owners.value.push(user.value.uid);
      invitedUsers.value.push({
        // email: user.value.email,
        ...foundUser,
        permission: 'owner'
      });
    }
  }

  const getDevices = async() => {
    devices.value = [];
    const response = await Device.get('/all?entity=' + selectedEntity.value);
    if (response.status === 'success') {
      for (const device of response.details) {
        if (device.active) {
        devices.value.push({
          id: device.id,
            name: device.friendlyName,
          });
        }
      }
    }

    grabbingDevices.value = false;
  }

  // Get the subcollections and their fields connected to the dataID.
  const getSubcollections = async(dataID) => {
    grabbingSubcollections.value = true;
    const response = await Data.get(dataID);
    if (response.statusCode === 200) {
      // Ignore subcollections that are not relevant to the timeseries.
      const ignoreSubcollections = ['Net_Cfg', 'action_request', 'action_result', 'location', 'messages', 'Alert_Database']
      subcollections.value = Object.fromEntries(
        Object.entries(response.body.details).filter(([key]) => !ignoreSubcollections.includes(key))
      );

      // Get all keys from the details object and filter out the ignored ones
      subcollectionOptions.value = Object.keys(response.body.details)
        .filter(key => !ignoreSubcollections.includes(key));
    }
    grabbingSubcollections.value = false;
    getSubcollectionFields();
  }

  const getSubcollectionFields = () => {
    if (source.value) {
      const selectedSubcollection = subcollections.value[source.value];
      if (!selectedSubcollection) return;
      subcollectionFields.value = Object.entries(selectedSubcollection)
        .filter(([, value]) => value === 'int' || value === 'float' || value === 'str')
        .map(([key]) => key).sort();
    }
  }

  const isStringField = computed(() => {
    if (!field.value || !source.value) return false;
    return subcollections.value[source.value]?.[field.value] === 'str';
  });

  const handleSubmit = async() => {
    loading.value = true;
    error.value = '';

    // convert alert days, hours, and minutes to seconds
    let totalSeconds = 0;
    if (!alertOnce.value) {
      totalSeconds = (days.value * 86400) + (hours.value * 3600) + (minutes.value * 60);
    }

    // Set up alert details
    const alertDetails = {
      alertID: props.alertDetails?.id || null,
      name: name.value,
      entity: selectedEntity.value,
      owners: owners.value,
      viewers: viewers.value,
      condition: { constant: constant.value, operator: operator.value, field: field.value },
      type: type.value,
      interval: totalSeconds,
      source: source.value,
      devices: selectedDevices.value,
      description: description.value,
      ...(level.value ? { level: level.value } : {}),
      validationCount: {
        activate: validationCountActivate.value,
        deactivate: validationCountDeactivate.value
      }
    }

    // Emit the new alert details and close the modal.
    emit('alert-details', alertDetails);
  }

  const close = () => {
    loading.value = false;
    emit('close');
  };

  const { onBackdropMousedown, onBackdropMouseup, onContentMousedown } = useModalBackdropClose(close);

  // Step navigation methods
  const nextStep = () => {
    if (!canProceed.value) {
      error.value = 'Please fill in all the required fields.';
      console.error('Create alert error:', error.value);
    }
    else if (currentStep.value < Object.keys(steps.value).length) {
      steps.value[currentStep.value].completed = true;
      currentStep.value++;
      canProceed.value = false;
      error.value = '';
    }
  };

  const prevStep = () => {
    if (currentStep.value > 1) {
      currentStep.value--;
    }
  };

  const goToStep = (stepIndex) => {
    if (steps.value[stepIndex]?.completed || steps.value[stepIndex-1]?.completed) {
      currentStep.value = Number(stepIndex);
    }
  };

  // User invitation methods
  const addUserInvite = () => {
    const user = users.value.find(user => user.id === addedUser.value);
    
    // Check if user is already invited
    const alreadyInvited = invitedUsers.value.some(invitedUser => invitedUser.userId === addedUser.value);
    
    if (user && !alreadyInvited) {
      invitedUsers.value.push({
        // email: user.email,
        ...user,
        permission: 'viewer', // Default to viewer
        // userId: addedUser.value
      });

      // Add the user to the viewers list by default
      viewers.value.push(addedUser.value);

      // Remove the user from the users array
      users.value = users.value.filter(listedUser => listedUser.id !== addedUser.value);
    }

    addedUser.value = '';
  };

  const updateUserPermission = (index, newPermission) => {
    const user = invitedUsers.value[index];
    const userId = user.id;
    
    // Remove user from both arrays first
    const ownerIndex = owners.value.indexOf(userId);
    const viewerIndex = viewers.value.indexOf(userId);
    
    if (ownerIndex > -1) {
      owners.value.splice(ownerIndex, 1);
    }
    if (viewerIndex > -1) {
      viewers.value.splice(viewerIndex, 1);
    }
    
    // Add user to the appropriate array based on new permission
    if (newPermission === 'owner') {
      owners.value.push(userId);
    } else {
      viewers.value.push(userId);
    }
  };

  const removeUserInvite = (index) => {
    const selectedUser = invitedUsers.value[index];
    const userId = selectedUser.id;

    // Remove user from owners/viewers arrays
    const ownerIndex = owners.value.indexOf(userId);
    const viewerIndex = viewers.value.indexOf(userId);
    
    if (ownerIndex > -1) {
      owners.value.splice(ownerIndex, 1);
    }
    if (viewerIndex > -1) {
      viewers.value.splice(viewerIndex, 1);
    }
    
    // Remove user from invited users list
    invitedUsers.value.splice(index, 1);

    // Add the user back to the users array
    users.value.push(selectedUser);
  };

  const saveNotificationChanges = async (notification, type) => {
    if (type === 'sms' && user.value.phone === '') {
        modal.value = 'verifyPhone';
        smsToggle.value = false;
        return;
    }

    const response = await Users.put('', {
        userUID: user.value.uid,
        notifications: {
            [notification]: {
                emailEnable: emailToggle.value,
                smsEnable: smsToggle.value,
            }
        },
    });
    if (response.status === 'success') {
        user.value.notifications[notification]['emailEnable'] = emailToggle.value;
        user.value.notifications[notification]['smsEnable'] = smsToggle.value;
        store.dispatch('setUser', {
            ...user.value,
            notifications: user.value.notifications,
        });
    }
    }

  const handlePhoneVerified = () => {
    modal.value = '';
    
    // Wait for store to be updated with phone number before proceeding
    const checkPhoneUpdated = () => {
      if (user.value.phone && user.value.phone !== '') {
        smsToggle.value = true;
        // Manually call save since @change won't fire on programmatic changes
        saveNotificationChanges('alerts', 'sms');
      } else {
        // Check again after a short delay
        setTimeout(checkPhoneUpdated, 50);
      }
    };
    
    // Start checking after a small initial delay
    setTimeout(checkPhoneUpdated, 100);
  }

  // Step validation
  watch([currentStep, name, selectedDevices, source, field, operator, alertOnce, days, hours, minutes, owners, invitedUsers], () => {
    switch (currentStep.value) {
      case 1: // Basic Info
        if (name.value.trim() !== '') {
          canProceed.value = true;
        } else {
          steps.value[1].completed = false;
          canProceed.value = false;
        }
        break;
      case 2: // Select Devices
        if (selectedDevices.value.length > 0) {
          canProceed.value = true;
        } else {
          steps.value[2].completed = false;
          canProceed.value = false;
        }
        break;
      case 3: // Set Conditions
        if (source.value !== '' && field.value !== '' && operator.value !== '') {
          canProceed.value = true;
        } else {
          steps.value[3].completed = false;
          canProceed.value = false;
        }
        break;
      case 4: // Alert Frequency
        if (alertOnce.value || (days.value > 0 || hours.value > 0 || minutes.value > 0)) {
          canProceed.value = true;
        } else {
          steps.value[4].completed = false;
          canProceed.value = false;
        }
        break;
      case 5: // Share & Review
        if (owners.value.length === 0) {
          steps.value[5].completed = false;
          canProceed.value = false;
          error.value = 'There must be at least one owner for the alert.';
        }
        else {
          steps.value[5].completed = true;
          canProceed.value = true;
          error.value = '';
        }
        break;
    }
  }, { deep: true, immediate: true });

  watch(selectedDevices, async() => {
    if (selectedDevices.value.length > 0) {
      await getSubcollections(selectedDevices.value[0]);
    }
  }, { immediate: true, deep: true });

  watch(source, () => {
    getSubcollectionFields();
  });

  watch(field, () => {
    if (isStringField.value) {
      if (operator.value === '>' || operator.value === '<') {
        operator.value = '';
      }
      if (typeof constant.value === 'number') {
        constant.value = '';
      }
    } else if (field.value) {
      if (typeof constant.value === 'string') {
        constant.value = 0;
      }
    }
  });

  watch(selectedEntity, async() => {
    await getUsers();
    await getDevices();
  }, { immediate: true });
</script>

<template>
  <div class="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex justify-center items-center z-50" @mousedown.self="onBackdropMousedown" @mouseup.self="onBackdropMouseup">
    <div class="ui-modal p-6 rounded-lg w-[700px] max-h-[calc(100vh-50px)] overflow-y-auto shadow-2xl" @mousedown="onContentMousedown">
      <!-- Header with step progress -->
      <div class="mb-6">
        <!-- Accent bar -->
        <div class="h-[3px] w-full bg-mc-red rounded-full mb-5"></div>

        <h2 v-if="!props.alertDetails" class="font-extrabold text-xl text-center mb-5 text-mc-ink dark:text-on-dark tracking-tight">Create A New Alert</h2>
        <h2 v-else class="font-extrabold text-xl text-center mb-5 text-mc-ink dark:text-on-dark tracking-tight">Edit Alert</h2>

        <!-- Connected step progress -->
        <div class="flex items-center mb-5">
          <template v-for="(stepData, index) in steps" :key="index">
            <div class="flex flex-col items-center flex-shrink-0">
              <span
                class="text-[9px] font-bold uppercase tracking-widest mb-1.5 text-center w-14 leading-tight"
                :class="{
                  'text-mc-red': currentStep == Number(index),
                  'text-green-600': stepData.completed && currentStep != Number(index),
                  'text-ink-muted dark:text-on-dark-muted': !stepData.completed && currentStep != Number(index)
                }"
              >{{ stepData.title }}</span>
              <div
                class="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold cursor-pointer transition-all"
                :class="{
                  'bg-mc-red text-white ring-4 ring-mc-red/20': currentStep == Number(index),
                  'bg-green-600 text-white': stepData.completed && currentStep != Number(index),
                  'bg-mc-cloud border border-mc-steel text-ink-muted dark:bg-white/[0.07] dark:border-white/20 dark:text-on-dark-muted': !stepData.completed && currentStep != Number(index)
                }"
                @click="goToStep(index)"
              >
                <span v-if="stepData.completed && currentStep != Number(index)">✓</span>
                <span v-else>{{ index }}</span>
              </div>
            </div>
            <!-- Connector line between steps (not after last) -->
            <div
              v-if="Number(index) < Object.keys(steps).length"
              class="flex-1 h-[1.5px] mt-4 mx-1 transition-colors"
              :class="stepData.completed ? 'bg-green-600' : 'bg-mc-steel dark:bg-white/10'"
            ></div>
          </template>
        </div>

        <!-- Divider + step title -->
        <div class="h-px w-full bg-mc-steel dark:bg-line-on-dark mb-4"></div>
        <h3 class="text-sm font-bold text-center text-mc-ink dark:text-on-dark uppercase tracking-widest">
          {{ steps[currentStep].title }}
        </h3>
      </div>

      <!-- Error Messages -->
      <div v-if="error" class="mt-5">
        <p class="font-normal text-mc-red whitespace-pre-line bg-mc-red-100 dark:bg-red-900/30 p-3 rounded-mc-sm">{{ error }}</p>
      </div>

      <form @submit.prevent="handleSubmit" class="min-h-[400px]">

        <!-- Step 1: Basic Information -->
        <div v-if="currentStep == 1" class="space-y-6">
          <div>
            <label for="alert-name" class="text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-1.5 block">Alert Name <span class="text-red-500">*</span></label>
            <input id="alert-name" v-model="name" type="text" maxlength="50" class="w-full p-3 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark focus-visible:outline-none focus-visible:border-mc-red focus-visible:ring-[3px] focus-visible:ring-mc-red/20"
                   placeholder="Enter a descriptive name for your alert" required />
            <p class="text-sm text-ink-muted dark:text-on-dark-muted mt-2">{{ nameLength }} characters remaining</p>
          </div>

          <!-- <div v-if="user.role === 'admin' || user.permissions.includes('viewSubentities')" class="bg-mc-cloud dark:bg-mc-charcoal p-4 rounded-mc-sm">
            <label class="font-bold block">
              Entity
              <select v-model="selectedEntity" class="w-full font-normal mt-2 p-3 border border-mc-steel rounded-mc-sm bg-mc-white text-mc-ink focus-visible:outline-none focus-visible:border-mc-blue focus-visible:ring-[3px] focus-visible:ring-mc-blue/40" required>
                <option v-for="(entityData, entityID) in entities" :key="entityID" :value="entityID">{{ entityData.name }}</option>
              </select>
            </label>
          </div> -->

          <div>
            <div class="flex items-center gap-2 mb-1.5">
              <label for="alert-message" class="text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted block">Alert Message</label>
              <InfoIcon
                content="This is the message you will see when you get the alert.

                Sources and values are automatically included, so no need to specify them."
              />
            </div>
            <textarea id="alert-message" v-model="description" maxlength="250" class="w-full p-3 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark focus-visible:outline-none focus-visible:border-mc-red focus-visible:ring-[3px] focus-visible:ring-mc-red/20"
                        rows="3" placeholder="Describe what this alert is for..."></textarea>
            <p class="text-sm text-ink-muted dark:text-on-dark-muted mt-2">{{ descriptionLength }} characters remaining</p>
          </div>

          <div>
            <label for="alert-level" class="text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-1.5 block">Alert Level</label>
            <select id="alert-level" v-model="level" class="w-full p-3 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark focus-visible:outline-none focus-visible:border-mc-red focus-visible:ring-[3px] focus-visible:ring-mc-red/20">
              <option value="">— Not set —</option>
              <option value="info">Info</option>
              <option value="warning">Warning</option>
              <option value="critical">Critical</option>
            </select>
          </div>
        </div>

        <!-- Step 2: Device Selection -->
        <div v-if="currentStep == 2">
          <p class="font-bold mb-3">
            Select the devices you want to monitor with this alert
            <span class="text-red-500">*</span>
          </p>

          <!-- Search bar -->
          <div class="relative mb-2">
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search by name or ID..."
              class="w-full pl-8 pr-3 py-2 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] focus-visible:outline-none focus-visible:border-mc-red focus-visible:ring-[3px] focus-visible:ring-mc-red/20 text-sm"
            />
            <span class="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted dark:text-on-dark-muted text-sm">⌕</span>
          </div>

          <!-- Sortable device table -->
          <div class="border border-mc-steel dark:border-line-on-dark rounded-mc-sm h-[260px] overflow-y-auto">
            <table class="w-full border-collapse">
              <thead class="bg-mc-cloud dark:bg-mc-charcoal sticky top-0">
                <tr>
                  <th class="w-8 p-2"></th>
                  <th
                    class="text-left p-2 text-xs uppercase tracking-wide cursor-pointer select-none"
                    :class="sortColumn === 'id' ? 'text-mc-red' : 'text-ink-muted dark:text-on-dark-muted'"
                    @click="toggleSort('id')"
                  >
                    ID
                    <span>{{ sortColumn === 'id' ? (sortDirection === 'asc' ? '↑' : '↓') : '↕' }}</span>
                  </th>
                  <th
                    class="text-left p-2 text-xs uppercase tracking-wide cursor-pointer select-none"
                    :class="sortColumn === 'name' ? 'text-mc-red' : 'text-ink-muted dark:text-on-dark-muted'"
                    @click="toggleSort('name')"
                  >
                    Name
                    <span>{{ sortColumn === 'name' ? (sortDirection === 'asc' ? '↑' : '↓') : '↕' }}</span>
                  </th>
                </tr>
              </thead>
              <tbody v-if="!grabbingDevices">
                <tr
                  v-for="device in filteredSortedDevices"
                  :key="device.id"
                  class="border-t border-mc-steel/40 dark:border-line-on-dark hover:bg-mc-cloud dark:hover:bg-mc-charcoal/60 cursor-pointer"
                  :class="selectedDevices.includes(device.id) ? 'bg-mc-red-100 dark:bg-red-900/10' : ''"
                  @click="toggleDevice(device.id)"
                >
                  <td class="p-2 text-center">
                    <input
                      type="checkbox"
                      :checked="selectedDevices.includes(device.id)"
                      @click.stop
                      @change="toggleDevice(device.id)"
                    />
                  </td>
                  <td class="p-2 text-sm text-ink-muted dark:text-on-dark-muted">{{ device.id }}</td>
                  <td class="p-2 text-sm">{{ device.name }}</td>
                </tr>
                <tr v-if="filteredSortedDevices.length === 0">
                  <td colspan="3" class="p-4 text-center text-sm text-ink-muted dark:text-on-dark-muted">
                    No devices match your search.
                  </td>
                </tr>
              </tbody>
              <tbody v-else>
                <tr>
                  <td colspan="3" class="p-4 text-center text-sm text-ink-muted dark:text-on-dark-muted">
                    Loading devices...
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Footer -->
          <div class="flex justify-between mt-2 text-xs text-ink-muted dark:text-on-dark-muted">
            <span>Showing {{ filteredSortedDevices.length }} of {{ devices.length }}</span>
            <span>Selected: {{ selectedDevices.length }} device(s)</span>
          </div>
        </div>

        <!-- Step 3: Condition Setup -->
        <div v-if="currentStep == 3" class="space-y-6">
          <div v-if="grabbingSubcollections" class="flex items-center justify-center py-8">
            <LoadingAnimation3 size="40px"></LoadingAnimation3>
            <span class="ml-3 text-ink-muted dark:text-on-dark-muted">Loading device data...</span>
          </div>
          
          <div v-else-if="selectedDevices.length === 0" class="bg-yellow-50 dark:bg-yellow-900/30 p-4 rounded-mc-sm">
            <p class="text-yellow-800 dark:text-yellow-200">Please select at least one device to configure conditions.</p>
          </div>
          
          <div v-else class="space-y-4">
            <div>
              <label for="data-source" class="text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-1.5 block">Data Source <span class="text-red-500">*</span></label>
              <select id="data-source" v-model="source" class="w-full p-3 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark focus-visible:outline-none focus-visible:border-mc-red focus-visible:ring-[3px] focus-visible:ring-mc-red/20" required>
                <option value="">Select a data source...</option>
                <option v-for="source in subcollectionOptions" :key="source" :value="source">{{ source }}</option>
              </select>
            </div>

            <div v-if="source" class="space-y-4">
              <p class="text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted">Alert Condition <span class="text-red-500">*</span></p>
              <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div>
                  <label for="condition-field" class="text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-1.5 block">Field</label>
                  <select id="condition-field" v-model="field" class="w-full p-2 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark focus-visible:outline-none focus-visible:border-mc-red focus-visible:ring-[3px] focus-visible:ring-mc-red/20" required>
                    <option value="">Select field...</option>
                    <option v-for="field in subcollectionFields" :key="field" :value="field">{{ field }}</option>
                  </select>
                </div>

                <div>
                  <label for="condition-operator" class="text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-1.5 block">Operator</label>
                  <select id="condition-operator" v-model="operator" class="w-full p-2 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark focus-visible:outline-none focus-visible:border-mc-red focus-visible:ring-[3px] focus-visible:ring-mc-red/20" required>
                    <option value="">Select operator...</option>
                    <option value="==">Equal to (=)</option>
                    <option value="!=">Not equal to (≠)</option>
                    <option v-if="!isStringField" value=">">Greater than (&gt;)</option>
                    <option v-if="!isStringField" value="<">Less than (&lt;)</option>
                  </select>
                </div>

                <div>
                  <label for="condition-value" class="text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-1.5 block">Value</label>
                  <input id="condition-value" v-model="constant"
                         :type="isStringField ? 'text' : 'number'"
                         :step="isStringField ? undefined : 'any'"
                         class="w-full p-2 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark focus-visible:outline-none focus-visible:border-mc-red focus-visible:ring-[3px] focus-visible:ring-mc-red/20"
                         placeholder="Enter value..." required />
                </div>
              </div>

              <div v-if="field && operator && constant !== ''" class="mt-4 p-3 bg-mc-cloud dark:bg-white/[0.04] border border-mc-steel dark:border-line-on-dark rounded-mc-sm">
                <p class="text-ink-muted dark:text-on-dark-muted text-sm">
                  <strong>Condition Preview:</strong> Alert when <code>{{ field }}</code> {{ operator }} <code>{{ constant }}</code>
                </p>
              </div>

              <div class="space-y-3">
                <div class="grid grid-cols-2 gap-4">
                  <div>
                    <div class="flex items-center gap-2 mb-1.5">
                      <label for="validation-activate" class="text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted">Activate</label>
                      <InfoIcon content="How many consecutive readings must match the condition before the alert is triggered. Increase this to filter out false positives from brief spikes." />
                    </div>
                    <input id="validation-activate" v-model.number="validationCountActivate" type="number" min="1" step="1"
                           class="w-full p-2 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark focus-visible:outline-none focus-visible:border-mc-red focus-visible:ring-[3px] focus-visible:ring-mc-red/20" />
                  </div>
                  <div>
                    <div class="flex items-center gap-2 mb-1.5">
                      <label for="validation-deactivate" class="text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted">Deactivate</label>
                      <InfoIcon content="How many consecutive readings must fail the condition before the alert is cleared. Increase this to prevent the alert from flickering off prematurely." />
                    </div>
                    <input id="validation-deactivate" v-model.number="validationCountDeactivate" type="number" min="1" step="1"
                           class="w-full p-2 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark focus-visible:outline-none focus-visible:border-mc-red focus-visible:ring-[3px] focus-visible:ring-mc-red/20" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Step 4: Alert Frequency -->
        <div v-if="currentStep == 4" class="space-y-6">
          <div class="space-y-4">
            <div class="flex items-center gap-2">
              <p class="text-sm font-bold text-mc-ink dark:text-on-dark uppercase tracking-widest">Alert Frequency</p>
              <InfoIcon
                content="You can set how often you want to receive alerts for the same condition. You can also choose to only alert once per event."
              />
              <span class="text-red-500">*</span>
            </div>
            <div class="flex items-center space-x-3 mb-4">
              <input type="checkbox" v-model="alertOnce" id="alertOnce" class="w-4 h-4 accent-mc-red" />
              <label for="alertOnce" class="font-medium">Only alert once per event</label>
            </div>

            <div v-if="!alertOnce" class="space-y-4">
              <div class="grid grid-cols-3 gap-4">
                <div>
                  <label for="freq-days" class="text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-1.5 block">Days</label>
                  <input id="freq-days" v-model="days" type="number" step="1" min="0"
                         class="w-full p-3 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark focus-visible:outline-none focus-visible:border-mc-red focus-visible:ring-[3px] focus-visible:ring-mc-red/20" />
                </div>
                <div>
                  <label for="freq-hours" class="text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-1.5 block">Hours</label>
                  <input id="freq-hours" v-model="hours" type="number" step="1" min="0"
                         class="w-full p-3 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark focus-visible:outline-none focus-visible:border-mc-red focus-visible:ring-[3px] focus-visible:ring-mc-red/20" />
                </div>
                <div>
                  <label for="freq-minutes" class="text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-1.5 block">Minutes</label>
                  <input id="freq-minutes" v-model="minutes" type="number" step="1" min="0"
                         class="w-full p-3 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark focus-visible:outline-none focus-visible:border-mc-red focus-visible:ring-[3px] focus-visible:ring-mc-red/20" />
                </div>
              </div>

              <div v-if="days > 0 || hours > 0 || minutes > 0" class="p-3 bg-green-50 dark:bg-green-900/30 rounded-mc-sm">
                <p class="text-green-800 dark:text-green-200 text-sm">
                  <strong>Frequency:</strong> Alert every {{ days }}d {{ hours }}h {{ minutes }}m
                </p>
              </div>
            </div>

            <div v-else class="p-3 bg-mc-cloud dark:bg-white/[0.04] border border-mc-steel dark:border-line-on-dark rounded-mc-sm">
              <p class="text-ink-muted dark:text-on-dark-muted text-sm">
                You will receive only one alert per unique event occurrence.
              </p>
            </div>
          </div>

          <div>
            <p class="text-sm text-orange-600 dark:text-orange-400 mb-4">* This will change your notification settings for all alerts.</p>
            <div class="flex flex-row gap-8">
              <!-- Email Permission switch -->
              <div class="flex items-center">
                <p class="text-sm text-ink-muted dark:text-on-dark-muted mr-2">Email</p>
                  <input
                      id="emailToggle"
                      type="checkbox"
                      v-model="emailToggle"
                      class="toggle-checkbox hidden"
                      @change="saveNotificationChanges('alerts', 'email')"
                  />
                  <label 
                      for="emailToggle" 
                      class="toggle-label block w-12 h-6 rounded-full bg-mc-steel relative"
                      :class="emailToggle ? 'bg-green-500' : 'bg-mc-steel'"
                  >
                      <span
                          class="absolute left-0 top-0 w-6 h-6 bg-white border border-gray-300 rounded-full transition-transform duration-200"
                          :class="emailToggle ? 'translate-x-6' : ''"
                      ></span>
                  </label>
              </div>

              <!-- SMS Permission switch -->
              <div class="flex items-center">
                <p class="text-sm text-ink-muted dark:text-on-dark-muted mr-2">SMS</p>
                  <input
                      id="smsToggle"
                      type="checkbox"
                      v-model="smsToggle"
                      class="toggle-checkbox hidden"
                      @change="saveNotificationChanges('alerts', 'sms')"
                  />
                  <label 
                      for="smsToggle" 
                      class="toggle-label block w-12 h-6 rounded-full bg-mc-steel relative"
                      :class="smsToggle ? 'bg-green-500' : 'bg-mc-steel'"
                  >
                      <span
                          class="absolute left-0 top-0 w-6 h-6 bg-white border border-gray-300 rounded-full transition-transform duration-200"
                          :class="smsToggle ? 'translate-x-6' : ''"
                      ></span>
                  </label>
              </div>
            </div>
          </div>
          <VerifyPhoneNumber v-if="modal === 'verifyPhone'" @close="modal = ''" @verified="handlePhoneVerified" />
        </div>

        <!-- Step 5: Share & Review -->
        <div v-if="currentStep == 5" class="space-y-6">
          <!-- User Invitations -->
          <div>
            <h4 class="text-sm font-bold text-mc-ink dark:text-on-dark uppercase tracking-widest mb-3">Share Alert Access</h4>
            <p class="text-sm text-ink-muted dark:text-on-dark-muted mb-4">
              Invite other users to receive or manage this alert. You can set their permissions after adding them.
            </p>

            <div class="flex gap-2 mb-4">
              <select v-model="addedUser" class="w-full p-3 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark focus-visible:outline-none focus-visible:border-mc-red focus-visible:ring-[3px] focus-visible:ring-mc-red/20">
                <option value="">Select a user...</option>
                <option v-for="user in users" :key="user.id" :value="user.id">{{ user.email }}</option>
              </select>
              <button type="button" @click="addUserInvite" class="btn-commerce px-5 py-2 text-xs">
                Invite
              </button>
            </div>

            <div v-if="invitedUsers.length > 0" class="space-y-2">
              <div v-for="(user, index) in invitedUsers" :key="index"
                   class="flex items-center justify-between p-2 bg-mc-white dark:bg-mc-charcoal rounded-mc-sm border border-mc-steel dark:border-line-on-dark">
                <div class="flex items-center gap-3">
                  <span class="font-medium">{{ user.email }}</span>
                  <select v-model="user.permission" @change="updateUserPermission(index, user.permission)"
                          class="pl-2 pr-7 py-1 text-sm border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark focus-visible:outline-none focus-visible:border-mc-red focus-visible:ring-[3px] focus-visible:ring-mc-red/20">
                    <option value="viewer">Viewer</option>
                    <option value="owner">Owner</option>
                  </select>
                </div>
                <button type="button" @click="removeUserInvite(index)"
                        class="text-mc-red hover:text-mc-red-700 text-sm font-semibold transition-colors duration-160">Remove</button>
              </div>
            </div>
          </div>

          <!-- Review Summary -->
          <div>
            <h4 class="text-sm font-bold text-mc-ink dark:text-on-dark uppercase tracking-widest mb-3">Alert Summary</h4>
            <div class="space-y-2 text-sm">
              <div><strong>Name:</strong> {{ name }}</div>
              <div><strong>Devices:</strong> {{ selectedDevices.length }} selected</div>
              <div v-if="source && field && operator"><strong>Condition:</strong> {{ field }} {{ operator }} {{ constant }}</div>
              <div><strong>Frequency:</strong> {{ alertOnce ? 'Once per event' : `Every ${days}d ${hours}h ${minutes}m` }}</div>
              <div v-if="description"><strong>Description:</strong> {{ description }}</div>
              <div v-if="level"><strong>Level:</strong> {{ level.charAt(0).toUpperCase() + level.slice(1) }}</div>
              <div><strong>Validation:</strong> Activates after {{ validationCountActivate }} reading(s), clears after {{ validationCountDeactivate }} reading(s)</div>
            </div>
          </div>
        </div>

        <!-- Navigation Buttons -->
        <div class="flex justify-between items-center mt-8 pt-6 border-t border-mc-steel dark:border-line-on-dark">
          <button type="button" @click="close" class="btn-ghost-dark px-5 py-2 text-xs">
            Cancel
          </button>
          
          <div class="flex gap-3">
            <button v-if="currentStep > 1" type="button" @click="prevStep" 
                    class="btn-ghost-dark px-5 py-2 text-xs">
              ← Previous
            </button>
            
            
            <button v-if="currentStep < Object.keys(steps).length" type="button" @click="nextStep" 
                    class="default-button">
              Next →
            </button>
            
            <div v-else-if="currentStep === Object.keys(steps).length" class="flex items-center">
              <LoadingAnimation3 v-if="loading" size="30px"></LoadingAnimation3>
              <div v-else>
                <button v-if="alertDetails" type="submit" :disabled="loading || !steps[currentStep].completed" 
                        class="default-button" 
                        :class="{'default-button-off': loading || !steps[currentStep].completed}">
                  Update Alert
                </button>
                <button v-else type="submit" :disabled="loading || !steps[currentStep].completed" 
                        class="default-button" 
                        :class="{'default-button-off': loading || !steps[currentStep].completed}">
                  Create Alert
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
    <div id="preview">

    </div>
  </div>
</template>
